/**
 * useYogaVideos — Loads & manages the multi-video library of a single yoga pose.
 * Pulls from /api/yoga-videos/{pose_id} which returns both default YouTube clips
 * and user-uploaded MP4s.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function useYogaVideos(poseId) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!poseId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/yoga-videos/${poseId}`);
      setVideos(r.data || []);
    } catch (e) {
      console.warn('[useYogaVideos] fetch failed:', e);
      setVideos([]);
    } finally {
      setLoading(false);
    }
  }, [poseId]);

  useEffect(() => { refresh(); }, [refresh]);

  const addYouTube = async (videoId, label = '') => {
    const fd = new FormData();
    fd.append('video_id', videoId);
    if (label) fd.append('label', label);
    await axios.post(`${API}/api/yoga-videos/${poseId}/youtube`, fd);
    await refresh();
  };

  const uploadFile = async (file, label = '') => {
    const fd = new FormData();
    fd.append('file', file);
    if (label) fd.append('label', label);
    await axios.post(`${API}/api/yoga-videos/${poseId}/upload`, fd);
    await refresh();
  };

  const toggleLock = async (videoId) => {
    await axios.put(`${API}/api/yoga-videos/${videoId}/lock`);
    await refresh();
  };

  const remove = async (videoId) => {
    await axios.delete(`${API}/api/yoga-videos/${videoId}`);
    await refresh();
  };

  return { videos, loading, refresh, addYouTube, uploadFile, toggleLock, remove };
}

// Parse various YouTube URL formats and return the video ID.
export function extractYouTubeId(input) {
  if (!input) return null;
  const trimmed = input.trim();
  // Already an ID (8-15 chars, alphanumeric + _ -)
  if (/^[\w-]{8,15}$/.test(trimmed)) return trimmed;
  const patterns = [
    /[?&]v=([\w-]{8,15})/,           // ?v=ID
    /youtu\.be\/([\w-]{8,15})/,       // youtu.be/ID
    /youtube\.com\/embed\/([\w-]{8,15})/,
    /youtube\.com\/shorts\/([\w-]{8,15})/,
    /youtube\.com\/v\/([\w-]{8,15})/,
  ];
  for (const p of patterns) {
    const m = trimmed.match(p);
    if (m) return m[1];
  }
  return null;
}
